import React, { useMemo } from 'react';
import { DNABox, DNAButton, DNAButtonProps, DNAText } from '@alucio/lux-ui';
import DNAPopover from 'src/components/DNA/Popover/DNAPopover';
import { useAppSettings } from 'src/state/context/AppSettings';
import { useContent } from 'src/state/context/ContentProvider/ContentProvider';
import { useContentPreviewModal } from '../State/ContentPreviewModalStateProvider';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';

interface MenuItem {
  iconName: string,
  tooltip: string,
  status: DNAButtonProps['status'],
  testID?: string,
}

const HEADER_ICONS = {
  ADD_TO_FOLDER:{
    iconName: 'folder-simple-plus',
    tooltip: 'Add file to folder',
    status: 'tertiary',
    testID: 'content-preview-header-add-to-folder-btn',
  },
  BOOKMARK: {
    iconName: 'star',
    tooltip: 'Add to favorites',
    status: 'tertiary',
    testID: 'bookmark-header-button',
  },
  BOOKMARKED: {
    iconName: 'star-fill',
    tooltip: 'Remove from favorites',
    status: 'primary',
    testID: 'bookmark-header-button',
  },
} satisfies Record<string, MenuItem>

const TopMenuBar:React.FC = () => {
  const {
    isModifiedDocumentVersion,
    isPublisher, activeDocumentVersionORM,
    activeDocumentORM,
    bookmarkDocument, addDocumentToFolder,
  } = useContentPreviewModal()
  const { activePresentation } = useContent();
  const { deviceMode } = useAppSettings();
  const enableFolderManagement = useFeatureFlags('enableFolderManagement');
  const isTablet = deviceMode === 'tablet';

  const displayingIcons = useMemo(() => {
    if (!activeDocumentORM) return [];
    const { addToFolder: canAddToFolder, bookmark } = activeDocumentORM.meta.permissions;
    const { isBookmarked } = activeDocumentORM.meta.bookmark;
    const canBookmark = bookmark && !isPublisher && !isModifiedDocumentVersion;
    const iconsArray: MenuItem[] = [
      ...canBookmark ? isBookmarked ? [HEADER_ICONS.BOOKMARKED] : [HEADER_ICONS.BOOKMARK] : [],
      ...canAddToFolder && enableFolderManagement ? [HEADER_ICONS.ADD_TO_FOLDER] : [],
    ]
    return iconsArray;
  }, [activeDocumentORM, isPublisher, isModifiedDocumentVersion, enableFolderManagement])

  const onOptionSelected = (option: string) => {
    if (option.startsWith('star')) {
      bookmarkDocument()
    }
    if (option === 'folder-simple-plus') {
      addDocumentToFolder()
    }
  }

  if (!activePresentation || !activeDocumentVersionORM || !activeDocumentORM) return null;
  const { latestPublishedDocumentVersionORM } = activeDocumentVersionORM.relations.documentORM.relations.version;

  if (latestPublishedDocumentVersionORM?.model.id !==
    activePresentation?.currentPresentablePage?.documentVersionORM.model.id) {
    return null;
  }

  return (
    <DNABox alignY="center" fill spacing="sm">
      {
        displayingIcons.map(({ iconName, tooltip, testID, status }) => {
          return (
            <DNAPopover key={iconName} disablePopover={['tabletPWA']}>
              <DNAPopover.Anchor>
                <DNAButton
                  key={`head_bar_icon_${iconName}`}
                  testID={testID}
                  appearance="ghost"
                  status={status}
                  size={isTablet ? 'xl' : 'lg'}
                  iconLeft={iconName}
                  onPress={() => onOptionSelected(iconName)}
                />
              </DNAPopover.Anchor>
              <DNAPopover.Content offset={2}>
                <DNAText numberOfLines={1} colorMode="dark">{tooltip}</DNAText>
              </DNAPopover.Content>
            </DNAPopover>
          )
        })
      }
    </DNABox>
  )
}

export default TopMenuBar
